import React, { useCallback, useRef, useState } from 'react'
import { debounce } from 'lodash'
import styled, { css } from 'styled-components'
// @ts-ignore
import { Tooltip } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { StreamData } from 'types/streamData'
import { AUTO_ATTR_KEY, AutomationId } from 'utils/automationUtil'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import { useResizeObserver } from 'hooks/useResizeObserver'
import { EllipsisStyles } from '../index.css'

interface StyledTitleLinkProps {
  mode: Theme
  fontSize: number
}

const TitleStyles = css`
  ${EllipsisStyles}
  font-size: ${({ fontSize }: { fontSize: number }) => sys.typescale[fontSize].size};
  font-style: normal;
  font-weight: 500;
  line-height: ${({ fontSize }: { fontSize: number }) => sys.typescale[fontSize].lineHeight};
  align-items: center;
  text-decoration: none;
  -webkit-line-clamp: 2;
`

const StyledTitleLink = styled.a.attrs({
  [AUTO_ATTR_KEY]: AutomationId.soundbiteDefaultFrame.title
})<StyledTitleLinkProps>`
  ${TitleStyles}
  color: ${({ mode }) => sys[mode].interaction.primary.default};
  -webkit-box-align: initial;
`

const StyledTitle = styled.div.attrs({
  [AUTO_ATTR_KEY]: AutomationId.soundbiteDefaultFrame.title
})<StyledTitleLinkProps>`
  ${TitleStyles}
  color: ${({ mode }) => sys[mode].text.primary};
`

type TitleProps = {
  streamData: StreamData
  fontSize?: number
}

const Title = ({ streamData, fontSize = 16 }: TitleProps) => {
  const theme = useDisplayTheme() as Theme
  const [displayTooltip, setDisplayTooltip] = useState(false)
  const cellContentRef = useRef<HTMLAnchorElement | HTMLDivElement>(null)

  const checkCellContent = () => {
    if (!cellContentRef.current) return
    const cellContent = cellContentRef.current

    if (!displayTooltip) {
      setDisplayTooltip(cellContent?.scrollHeight > cellContent?.clientHeight)
    }
  }

  useResizeObserver(
    cellContentRef,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useCallback(debounce(checkCellContent, 10), [])
  )

  const { title, url } = streamData

  return url ? (
    <StyledTitleLink
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => {
        event.stopPropagation()
      }}
      mode={theme}
      ref={cellContentRef as React.RefObject<HTMLAnchorElement>}
      fontSize={fontSize}>
      <Tooltip mode={theme} enabled={displayTooltip} content={title}>
        <span>{title}</span>
      </Tooltip>
    </StyledTitleLink>
  ) : (
    <StyledTitle
      mode={theme}
      ref={cellContentRef as React.RefObject<HTMLDivElement>}
      fontSize={fontSize}>
      <Tooltip mode={theme} enabled={displayTooltip} content={title}>
        <span>{title}</span>
      </Tooltip>
    </StyledTitle>
  )
}

export default Title
